import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Download,
  PencilLine,
  Search,
} from 'lucide-react';
import { bdeseService } from '../services/bdese.service';
import {
  LIBELLE_CATEGORIE,
  type DefinitionIndicateur,
  type PointDeDonnees,
  type RapportConsulte,
  type Referentiel,
  type RubriqueReferentiel,
} from '../types';

interface LigneAffichee {
  point: PointDeDonnees;
  definition?: DefinitionIndicateur;
}

interface GroupeAffiche {
  code: string;
  libelle: string;
  lignes: LigneAffichee[];
}

/**
 * La consultation d'un exercice — l'écran que les élus ouvrent.
 *
 * Le référentiel reste la source de vérité, mais ses codes ne sont plus
 * l'interface. L'écran affiche d'abord des rubriques et des libellés humains.
 * Les articles, sources et détails de calcul restent disponibles à la demande :
 * ils sont indispensables pour défendre le document, pas pour chaque lecture.
 */
export function BdeseConsultationPage() {
  const { folderUuid = '', reportUuid = '' } = useParams();
  const [donnees, setDonnees] = useState<RapportConsulte | null>(null);
  const [referentiel, setReferentiel] = useState<Referentiel | null>(null);
  const [chargement, setChargement] = useState(true);
  const [autorise, setAutorise] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [peutModifier, setPeutModifier] = useState(false);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  const charger = useCallback(async () => {
    if (!reportUuid) return;
    setChargement(true);
    setErreurChargement(null);
    try {
      const [rapport, droits, definitions] = await Promise.all([
        bdeseService.consulter(reportUuid),
        folderUuid
          ? bdeseService.lireModalites(folderUuid).catch(() => null)
          : Promise.resolve(null),
        // Une indisponibilité ponctuelle du référentiel ne doit pas retirer les
        // valeurs du rapport. Le repli humanise alors leurs codes.
        bdeseService.referentiel(reportUuid).catch(() => null),
      ]);
      setDonnees(rapport);
      setReferentiel(definitions);
      setPeutModifier(droits?.modifiable === true);
      setAutorise(true);
    } catch (e: unknown) {
      const statut = (e as { response?: { status?: number } })?.response
        ?.status;
      setAutorise(statut !== 401 && statut !== 403);
      setDonnees(null);
      setReferentiel(null);
      setPeutModifier(false);
      if (statut !== 401 && statut !== 403) {
        setErreurChargement(
          'Cet exercice ne peut pas être chargé pour le moment.'
        );
      }
    } finally {
      setChargement(false);
    }
  }, [folderUuid, reportUuid]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const points = useMemo(
    () => donnees?.pointsDeDonnees ?? [],
    [donnees?.pointsDeDonnees]
  );
  const groupes = useMemo(
    () => grouperPourLecture(points, referentiel, recherche),
    [points, referentiel, recherche]
  );
  const nombreVisible = useMemo(
    () => groupes.reduce((total, groupe) => total + groupe.lignes.length, 0),
    [groupes]
  );

  const telecharger = async (format: 'pdf' | 'xlsx') => {
    setMessage(null);
    try {
      const { blob, nom } = await bdeseService.telecharger(reportUuid, format);
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = nom;
      lien.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage("Le document n'a pas pu être produit.");
    }
  };

  if (!autorise) {
    return (
      <p className="py-8 text-sm text-neutral-500">
        Cet exercice ne vous est pas ouvert.
      </p>
    );
  }
  if (chargement) {
    return <p className="py-8 text-sm text-neutral-500">Chargement…</p>;
  }
  if (erreurChargement) {
    return (
      <div className="py-8" role="alert">
        <p className="text-sm text-red-800">{erreurChargement}</p>
        <button
          type="button"
          onClick={() => void charger()}
          className="focus-visible:ring-primary-600 mt-3 min-h-11 rounded-lg px-3 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
        >
          Réessayer
        </button>
      </div>
    );
  }
  if (!donnees) return null;

  const rapport = donnees.rapport;
  const rechercheActive = recherche.trim().length > 0;

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to={`/bdese/${folderUuid}`}
        className="mb-4 inline-flex min-h-11 items-center gap-2 text-sm text-neutral-500 hover:text-neutral-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Tous les exercices
      </Link>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            Exercice {rapport.annee}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Informations économiques, sociales et environnementales
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {peutModifier && (
            <Link
              to={`/bdese/${folderUuid}/exercice/${reportUuid}/completer`}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <PencilLine className="h-4 w-4" />
              Compléter
            </Link>
          )}
          <button
            type="button"
            onClick={() => void telecharger('xlsx')}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Download className="h-4 w-4" />
            Excel
          </button>
          <button
            type="button"
            onClick={() => void telecharger('pdf')}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
            style={{
              backgroundColor: 'var(--marque-accent)',
              color: 'var(--marque-sur-accent)',
            }}
          >
            <Download className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {message && (
        <p
          className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800"
          role="status"
        >
          {message}
        </p>
      )}

      {points.length > 0 && (
        <div className="relative mb-4 max-w-md">
          <Search className="absolute top-3 left-3 h-4 w-4 text-neutral-400" />
          <input
            type="search"
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
            aria-label="Rechercher dans l'exercice"
            placeholder="Rechercher une information ou un article"
            className="min-h-11 w-full rounded-xl border py-2 pr-3 pl-9 text-base shadow-sm placeholder:text-neutral-400/50 focus:ring-2 focus:outline-none sm:text-sm"
            style={{
              borderColor: 'var(--marque-trait)',
              outlineColor: 'var(--marque-accent)',
            }}
          />
        </div>
      )}

      {groupes.length === 0 ? (
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <p className="text-sm text-neutral-500">
            {points.length === 0
              ? "Cet exercice ne contient pas encore d'informations."
              : 'Aucune information ne correspond à cette recherche.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rechercheActive && (
            <p className="text-sm text-neutral-500" role="status">
              {nombreVisible} résultat{nombreVisible > 1 ? 's' : ''}
            </p>
          )}
          {groupes.map(groupe => (
            <details
              key={groupe.code}
              open={rechercheActive || undefined}
              className="group overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
            >
              <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 px-5 py-3 text-left hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset">
                <span>
                  <span className="block text-sm font-semibold text-neutral-900">
                    {groupe.libelle}
                  </span>
                  <span className="mt-0.5 block text-xs text-neutral-500">
                    {groupe.lignes.length} information
                    {groupe.lignes.length > 1 ? 's' : ''}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-open:rotate-180" />
              </summary>

              <ul className="border-t border-neutral-100">
                {groupe.lignes.map(({ point, definition }, index) => (
                  <li
                    key={`${point.indicateurCode}-${point.anneePeriode}-${index}`}
                    className="border-b border-neutral-100 px-5 py-4 last:border-b-0"
                  >
                    <p className="text-sm font-medium text-neutral-900">
                      {definition?.libelle ??
                        humaniserCode(point.indicateurCode)}
                    </p>
                    <ValeurLisible point={point} definition={definition} />

                    {(point.anomalie || (point.alertes?.length ?? 0) > 0) && (
                      <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                          {[point.anomalie, ...(point.alertes ?? [])]
                            .filter(Boolean)
                            .join(' ')}
                        </span>
                      </div>
                    )}

                    <details className="mt-2 text-xs text-neutral-500">
                      <summary className="inline-flex min-h-8 cursor-pointer items-center py-1 font-medium text-neutral-500 hover:text-neutral-800">
                        Source et fondement
                      </summary>
                      <dl className="mt-1 grid gap-x-4 gap-y-1 border-l-2 border-neutral-100 pl-3 sm:grid-cols-[max-content_1fr]">
                        <dt>Source</dt>
                        <dd className="text-neutral-700">
                          {libelleSource(point.source)}
                        </dd>
                        <dt>Article</dt>
                        <dd className="text-neutral-700">
                          {definition?.cheminArticle || point.cheminArticle}
                        </dd>
                        {point.anneePeriode !== rapport.annee && (
                          <>
                            <dt>Période</dt>
                            <dd className="text-neutral-700">
                              {point.anneePeriode}
                            </dd>
                          </>
                        )}
                      </dl>
                    </details>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}

      <details className="mt-5 max-w-3xl text-xs text-neutral-500">
        <summary className="inline-flex min-h-9 cursor-pointer items-center py-1 font-medium hover:text-neutral-800">
          Informations juridiques sur cet exercice
        </summary>
        <dl className="mt-1 grid gap-x-4 gap-y-1 border-l-2 border-neutral-200 pl-3 sm:grid-cols-[max-content_1fr]">
          <dt>Entreprise</dt>
          <dd className="text-neutral-700">
            {LIBELLE_CATEGORIE[rapport.categorieSeuil]}
          </dd>
          <dt>Référentiel</dt>
          <dd className="text-neutral-700">
            {rapport.referentielCode}, version {rapport.referentielVersion}
          </dd>
          {typeof donnees.fondementAcces === 'string' && (
            <>
              <dt>Accès</dt>
              <dd className="text-neutral-700">{donnees.fondementAcces}</dd>
            </>
          )}
        </dl>
      </details>
    </div>
  );
}

function grouperPourLecture(
  points: PointDeDonnees[],
  referentiel: Referentiel | null,
  recherche: string
): GroupeAffiche[] {
  const definitions = new Map<
    string,
    { definition: DefinitionIndicateur; rubrique: RubriqueReferentiel }
  >();
  for (const rubrique of referentiel?.rubriques ?? []) {
    for (const definition of rubrique.indicateurs) {
      if (!definitions.has(definition.code)) {
        definitions.set(definition.code, { definition, rubrique });
      }
    }
  }

  const groupes = new Map<string, GroupeAffiche>();
  const q = recherche.trim().toLocaleLowerCase('fr');

  for (const point of points) {
    const trouvee = definitions.get(point.indicateurCode);
    const definition = trouvee?.definition;
    const correspond = [
      definition?.libelle,
      point.indicateurCode,
      definition?.cheminArticle,
      point.cheminArticle,
    ]
      .filter(Boolean)
      .some(texte => String(texte).toLocaleLowerCase('fr').includes(q));
    if (q && !correspond) continue;

    const code = trouvee?.rubrique.code ?? 'autres-informations';
    const groupe = groupes.get(code) ?? {
      code,
      libelle: trouvee?.rubrique.libelle ?? 'Autres informations',
      lignes: [],
    };
    groupe.lignes.push({ point, definition });
    groupes.set(code, groupe);
  }

  const ordre = new Map(
    (referentiel?.rubriques ?? []).map((rubrique, index) => [
      rubrique.code,
      index,
    ])
  );
  return [...groupes.values()].sort(
    (a, b) =>
      (ordre.get(a.code) ?? Number.MAX_SAFE_INTEGER) -
      (ordre.get(b.code) ?? Number.MAX_SAFE_INTEGER)
  );
}

function ValeurLisible({
  point,
  definition,
}: {
  point: PointDeDonnees;
  definition?: DefinitionIndicateur;
}) {
  if (point.mention) {
    return <p className="mt-1 text-sm text-neutral-700">{point.mention}</p>;
  }
  if (point.motifIndisponible) {
    return (
      <p className="mt-1 text-sm text-neutral-600">
        Non renseigné — {point.motifIndisponible}
      </p>
    );
  }
  if (point.valeurNumerique !== null && point.valeurNumerique !== undefined) {
    return (
      <p className="mt-1 text-sm text-neutral-700">
        {formaterNombre(point.valeurNumerique)}
        {definition?.unite ? ` ${definition.unite}` : ''}
      </p>
    );
  }
  if (point.valeurTexte !== null && point.valeurTexte !== undefined) {
    return <p className="mt-1 text-sm text-neutral-700">{point.valeurTexte}</p>;
  }

  const valeur = point.valeurJson ?? point.valeur;
  if (valeur !== null && typeof valeur === 'object') {
    const total = !Array.isArray(valeur)
      ? (valeur as Record<string, unknown>).total
      : undefined;
    const details = aplatirStructure(valeur).filter(
      ligne => ligne.chemin.join('.') !== 'total'
    );
    return (
      <div className="mt-1 text-sm text-neutral-700">
        <p>
          {typeof total === 'number'
            ? `${formaterNombre(total)}${definition?.unite ? ` ${definition.unite}` : ''}`
            : details.length > 0
              ? `${details.length} élément${details.length > 1 ? 's' : ''} détaillé${details.length > 1 ? 's' : ''}`
              : 'Aucune valeur renseignée.'}
        </p>
        {details.length > 0 && (
          <details className="mt-2">
            <summary className="inline-flex min-h-8 cursor-pointer items-center py-1 text-xs font-medium text-neutral-500 hover:text-neutral-800">
              Voir le détail
            </summary>
            <dl className="mt-1 grid gap-x-4 gap-y-1 border-l-2 border-neutral-100 pl-3 sm:grid-cols-[minmax(10rem,1fr)_auto]">
              {details.map((ligne, index) => (
                <div
                  key={`${ligne.chemin.join('.')}-${index}`}
                  className="contents"
                >
                  <dt className="text-neutral-500">
                    {libelleChemin(ligne.chemin)}
                  </dt>
                  <dd className="text-left font-medium text-neutral-700 sm:text-right">
                    {formaterValeurSimple(ligne.valeur)}
                  </dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </div>
    );
  }

  return (
    <p className="mt-1 text-sm text-neutral-600">
      {valeur === null || valeur === undefined
        ? 'Non renseigné.'
        : formaterValeurSimple(valeur)}
    </p>
  );
}

interface ValeurAplatie {
  chemin: string[];
  valeur: string | number | boolean;
}

function aplatirStructure(
  valeur: unknown,
  chemin: string[] = [],
  profondeur = 0
): ValeurAplatie[] {
  if (valeur === null || valeur === undefined || profondeur > 5) return [];
  if (['string', 'number', 'boolean'].includes(typeof valeur)) {
    return [{ chemin, valeur: valeur as string | number | boolean }];
  }
  if (Array.isArray(valeur)) {
    return valeur.flatMap((element, index) =>
      aplatirStructure(element, [...chemin, String(index + 1)], profondeur + 1)
    );
  }
  return Object.entries(valeur as Record<string, unknown>).flatMap(
    ([cle, enfant]) =>
      aplatirStructure(enfant, [...chemin, cle], profondeur + 1)
  );
}

const LIBELLES_CLES: Record<string, string> = {
  F: 'Femmes',
  M: 'Hommes',
  cdi: 'CDI',
  cdd: 'CDD',
  cadre: 'Cadres',
  technicien: 'Techniciens',
  am: 'Agents de maîtrise',
  employe: 'Employés',
  ouvrier: 'Ouvriers',
  apprentissage: 'Apprentissage',
  effectif: 'Effectif',
  inscrits: 'Inscrits',
  bulletins: 'Bulletins',
  moyenneBrute: 'Moyenne brute',
  sommeDesDouzeMois: 'Somme des douze mois',
  total: 'Total',
};

const MOIS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

function libelleChemin(chemin: string[]): string {
  return chemin
    .filter(
      cle => !['repartition', 'detail', 'parMois', 'parCsp'].includes(cle)
    )
    .flatMap(cle => {
      if (/^(?:[1-9]|1[0-2])$/.test(cle) && chemin.includes('parMois')) {
        return [MOIS[Number(cle) - 1]];
      }
      return cle.split('_').map(morceau => {
        const connu = LIBELLES_CLES[morceau];
        if (connu) return connu;
        const espace = morceau.replace(/([a-zà-ÿ])([A-Z])/g, '$1 $2');
        return espace.charAt(0).toUpperCase() + espace.slice(1).toLowerCase();
      });
    })
    .join(' · ');
}

function humaniserCode(code: string): string {
  return libelleChemin(code.split('.'));
}

function formaterNombre(valeur: number): string {
  return valeur.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

function formaterValeurSimple(valeur: unknown): string {
  if (typeof valeur === 'number') return formaterNombre(valeur);
  if (typeof valeur === 'boolean') return valeur ? 'Oui' : 'Non';
  return String(valeur);
}

function libelleSource(source: string): string {
  const sources: Record<string, string> = {
    paie: 'Données de paie',
    calcul_automatique: 'Calculée depuis les données de paie',
    entreprise: "Renseignée par l'entreprise",
    indisponible: 'Information indisponible',
    confidentiel: 'Information confidentielle',
  };
  return sources[source] ?? humaniserCode(source);
}
